/**
 * useBlocksData Hook
 *
 * Provides blocks data with pagination and mutations for Content Manager.
 * Wraps the block-content domain operations in a React Query interface.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { usePaginatedQuery } from './UsePaginatedQuery'
import { useOptimisticDelete } from './UseOptimisticMutation'
import { useNavigate } from 'react-router-dom'
import { useCallback, useEffect } from 'react'
import type { PlayBlockType } from '@/types/blocks'
import type { RootBlockDTO } from '@/types/blocks'
import {
	blockKeys,
	fetchBlocksPage,
	moveBlockToFolder as apiMoveToFolder,
	permanentDeleteBlock as apiPermanentDelete,
	restoreBlock as apiRestore,
	toggleBlockStar as apiToggleStar,
	trashBlock as apiTrash,
} from '../api/queries/BlockQueries'

interface UseBlocksDataOptions {
	section?: 'all' | 'starred' | 'trash' | 'recents'
	playbookId?: string
	folderId?: string | null
	blockTypes?: PlayBlockType[]
	teamId?: string | null
}

interface UseBlocksDataReturn {
	blocks: RootBlockDTO[]
	isLoading: boolean
	error: string | null
	fetchNextPage: () => void
	hasNextPage: boolean
	isFetchingNextPage: boolean
	toggleStar: (id: string) => Promise<void>
	trash: (id: string) => Promise<void>
	restore: (id: string) => Promise<void>
	moveToFolder: (id: string, folderId: string | null) => Promise<void>
	permanentDelete: (id: string) => Promise<void>
	refetch: () => void
}

export function useBlocksData(options: UseBlocksDataOptions = {}): UseBlocksDataReturn {
	const {
		section = 'all',
		playbookId,
		folderId,
		blockTypes,
		teamId,
	} = options

	const navigate = useNavigate()
	const queryClient = useQueryClient()

	const handleUnauthorized = useCallback((error: Error) => {
		if (error.message === 'UNAUTHORIZED') {
			navigate('/login')
		}
	}, [navigate])

	// Build query params
	const queryParams = {
		section,
		playbookId,
		folderId,
		blockTypes,
		teamId,
	}

	// Paginated query
	const {
		items: blocks,
		isLoading,
		error: queryError,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		refetch: refetchQuery,
	} = usePaginatedQuery<RootBlockDTO>({
		queryKey: blockKeys.list(queryParams),
		fetchPage: async (cursor) => {
			const result = await fetchBlocksPage({
				...queryParams,
				limit: 20,
				cursor,
			})
			return {
				items: result.items,
				nextCursor: result.nextCursor ?? undefined,
				hasMore: result.hasMore,
			}
		},
		getItemId: (block) => block.id,
	})

	// Handle unauthorized errors
	useEffect(() => {
		if (queryError) {
			handleUnauthorized(queryError as Error)
		}
	}, [queryError, handleUnauthorized])

	// TOGGLE STAR MUTATION
	const toggleStarMutation = useMutation({
		mutationFn: apiToggleStar,
		onError: handleUnauthorized,
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: blockKeys.lists() })
		},
	})

	// TRASH MUTATION (optimistic)
	const trashMutation = useOptimisticDelete<RootBlockDTO, string>({
		mutationFn: (id: string) => apiTrash(id),
		queryKey: blockKeys.list(queryParams),
		getId: (block) => block.id,
		onError: handleUnauthorized,
	})

	// RESTORE MUTATION
	const restoreMutation = useMutation({
		mutationFn: apiRestore,
		onError: handleUnauthorized,
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: blockKeys.lists() })
		},
	})

	// MOVE TO FOLDER MUTATION
	const moveToFolderMutation = useMutation({
		mutationFn: ({ id, folderId }: { id: string; folderId: string | null }) =>
			apiMoveToFolder(id, folderId),
		onError: handleUnauthorized,
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: blockKeys.lists() })
		},
	})

	// PERMANENT DELETE MUTATION (optimistic)
	const permanentDeleteMutation = useOptimisticDelete<RootBlockDTO, string>({
		mutationFn: (id: string) => apiPermanentDelete(id),
		queryKey: blockKeys.list(queryParams),
		getId: (block) => block.id,
		onError: handleUnauthorized,
	})

	// WRAPPER FUNCTIONS
	const toggleStar = async (id: string): Promise<void> => {
		await toggleStarMutation.mutateAsync(id)
	}

	const trash = async (id: string): Promise<void> => {
		await trashMutation(id)
	}

	const restore = async (id: string): Promise<void> => {
		await restoreMutation.mutateAsync(id)
	}

	const moveToFolder = async (id: string, folderId: string | null): Promise<void> => {
		await moveToFolderMutation.mutateAsync({ id, folderId })
	}

	const permanentDelete = async (id: string): Promise<void> => {
		await permanentDeleteMutation(id)
	}

	const refetch = useCallback(() => {
		refetchQuery()
	}, [refetchQuery])

	return {
		blocks,
		isLoading,
		error: queryError?.message || null,
		fetchNextPage,
		hasNextPage: hasNextPage ?? false,
		isFetchingNextPage,
		toggleStar,
		trash,
		restore,
		moveToFolder,
		permanentDelete,
		refetch,
	}
}
